import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Container,
  Typography,
  CircularProgress,
  Box,
  Grid,
} from "@mui/material";
import { get, del } from "src/utils/networkUtils";
import UsersSignupChart from "src/components/users/UsersSignupChart";
import UsersCountSummary from "src/components/users/UsersCountSummary";
import ProgressBarSummary from "src/components/users/ProgressBarSummary";
import { fCurrency } from "src/utils/formatNumber";
import StoreTypeItemsTable from "src/components/store-item/StoreTypeItemsTable";
import StoreItemDialog from "src/components/store-item/StoreItemDialog";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setStoreItems } from "src/store/slices/storeItemsSlice";
import useWithUrlTenant from "src/hooks/useTenant";

export default function CoinsDashboardPage() {
  const [loading, setLoading] = useState(true);
  const [purchasesLoading, setPurchasesLoading] = useState(true);
  const [storeItemsLoading, setStoreItemsLoading] = useState(true);
  const [purchasesStats, setPurchasesStats] = useState(null);
  const [storeItems, setStoreItemsLocal] = useState(null);
  const [storeRevenue, setStoreRevenue] = useState(null);
  const [totalItems, setTotalItems] = useState(null);
  const [totalRevenue, setTotalRevenue] = useState(null);
  const [storeItemsByType, setStoreItemsByTypeState] = useState(null);
  const [openPrizeDrawDialog, setOpenPrizeDrawDialog] = useState(false);
  const [item, setItem] = useState(null);
  const withTenant = useWithUrlTenant();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      try {
        const [purchasesResponse, storeItemsResponse, storeTypeResponse] =
          await Promise.all([
            get(withTenant(`/admin/marketplace/purchases/stats`)).catch(
              () => null
            ),
            get(
              withTenant(`/admin/marketplace/store-items/purchases?limit=11`)
            ).catch(() => null),
            get(withTenant(`/admin/marketplace/store-items-by-types`)).catch(
              () => null
            ),
          ]);

        if (purchasesResponse) setPurchasesStats(purchasesResponse.stats);

        if (storeItemsResponse) {
          setStoreItemsLocal(storeItemsResponse.topItems);
          setStoreRevenue(storeItemsResponse.topProducts);
          setTotalItems(storeItemsResponse.totalItems);
          setTotalRevenue(storeItemsResponse.totalRevenue);
        }

        if (storeTypeResponse) {
          setStoreItemsByTypeState(storeTypeResponse.items);

          dispatch(setStoreItems(storeTypeResponse.items));
        }
      } catch (error) {
        console.error("Error loading dashboard data:", error);
      } finally {
        setLoading(false);
        setPurchasesLoading(false);
        setStoreItemsLoading(false);
      }
    };

    loadData();
  }, [dispatch, withTenant]);

  const handleCloseStageDialog = () => {
    setItem(null);
    setOpenPrizeDrawDialog(false);
  };
  const handleSuccessStageDialog = () => window.location.reload(true);

  const handleOpenEditStageDialog = (row) => {
    navigate(`/dashboard/store-item/${row.marketplaceStoreItemId}`);
  };

  const handleDeleteStoreItem = async (row) => {
    try {
      await del(`/admin/marketplace/store-items/${row.marketplaceStoreItemId}`);
      window.location.reload(true);
    } catch (error) {
      console.error("Failed to delete item:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title> Store - Coins/Spins/Pass </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" mb={1}>
          <Typography variant="h4" gutterBottom>
            Store - Coins/Spins/Pass
          </Typography>
        </Stack>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress size={60} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <Grid container spacing={3}>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Paying Users"
                    total={purchasesStats?.totalPayUsers ?? 0}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Paying Users %"
                    total={`${
                      purchasesStats?.totalPayUsers
                        ? Math.round(
                            (purchasesStats?.totalPayUsers /
                              purchasesStats?.totalUsers) *
                              10000
                          ) / 100 ?? 0
                        : 0
                    }%`}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Avg Rev/ User"
                    total={`$${purchasesStats?.avgProfitPerPayUser ?? 0}`}
                  />
                </Grid>
                <Grid item xs={3}>
                  <UsersCountSummary
                    title="Revenue / User"
                    total={`$${purchasesStats?.avgProfitPerAllUsers ?? 0}`}
                  />
                </Grid>
                <Grid item xs={6}>
                  {storeItemsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                        border: 1,
                        borderColor: "grey.300",
                        borderRadius: 1,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : storeRevenue ? (
                    <ProgressBarSummary
                      title="Total Revenue"
                      total={`${fCurrency(
                        Math.round(totalRevenue / 100) ?? 0
                      )}`}
                      sx={{ mb: 2 }}
                      data={(() => {
                        const maxRevenue = Math.max(
                          ...storeRevenue.map((item) => item.totalPrice)
                        );
                        return storeRevenue.map((item) => ({
                          title: item.productId,
                          amount: `${fCurrency(
                            Math.round(item.totalPrice / 100)
                          )}`,
                          pct: Math.round((item.totalPrice / maxRevenue) * 80),
                          store: item.store,
                        }));
                      })()}
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        border: 1,
                        borderColor: "grey.300",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Failed to load revenue data
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={6}>
                  {storeItemsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 200,
                        border: 1,
                        borderColor: "grey.300",
                        borderRadius: 1,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : storeItems ? (
                    <ProgressBarSummary
                      title="Unit Sales"
                      total={totalItems ?? 0}
                      sx={{ mb: 2 }}
                      data={(() => {
                        const maxItems = Math.max(
                          ...storeItems.map((item) => item.purchaseCount)
                        );
                        return storeItems.map((item) => ({
                          title: item.productId,
                          amount: item.purchaseCount,
                          pct: Math.round((item.purchaseCount / maxItems) * 80),
                          store: item.store,
                        }));
                      })()}
                    />
                  ) : (
                    <Box
                      sx={{
                        p: 3,
                        textAlign: "center",
                        border: 1,
                        borderColor: "grey.300",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Failed to load store items data
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card>
                    {purchasesLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: 300,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : purchasesStats ? (
                      <UsersSignupChart
                        title="Revenue Per Day"
                        subheader=""
                        height={280}
                        chartLabels={
                          purchasesStats.profitPerDay?.map(
                            (date) => date.date
                          ) || []
                        }
                        chartData={[
                          {
                            name: "Revenue ($)",
                            type: "column",
                            fill: "solid",
                            data:
                              purchasesStats.profitPerDay?.map(
                                (date) => date.profit
                              ) || [],
                          },
                        ]}
                      />
                    ) : (
                      <Box sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          Failed to load revenue data
                        </Typography>
                      </Box>
                    )}
                  </Card>
                </Grid>

                <Grid item xs={12}>
                  <UsersCountSummary
                    title="Sales Units / Paying users"
                    total={purchasesStats?.avgPruchasePerUser ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="First Time Buyers"
                    total={purchasesStats?.oneTimeBuyers ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="First Time Buyers %"
                    total={purchasesStats?.oneTimePayPct ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Recurring Buyers"
                    total={purchasesStats?.multipleTimeBuyers ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Recurring Buyers %"
                    total={purchasesStats?.multipleTimePayPct ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Avg Days Before First Purchase"
                    total={purchasesStats?.avgDaysBeforeFirstPurchase ?? 0}
                  />
                </Grid>
                <Grid item xs={6}>
                  <UsersCountSummary
                    title="Avg Days Between Purchases"
                    total={purchasesStats?.avgDaysBetweenPurchases ?? 0}
                  />
                </Grid>
              </Grid>
            </Grid>

          </Grid>
        )}

        {!loading &&
          storeItemsByType &&
          Object.entries(storeItemsByType).map(([type, items]) => (
            <Box key={type}>
              <StoreTypeItemsTable
                type={type}
                rows={items}
                onEdit={handleOpenEditStageDialog}
                onDelete={handleDeleteStoreItem}
                isViewer={false}
              />
            </Box>
          ))}

        {!loading &&
          storeItemsByType &&
          Object.entries(storeItemsByType).map(([type, items]) => (
            <Box key={type}>
              <StoreTypeItemsTable
                type={type}
                rows={items}
                onEdit={handleOpenEditStageDialog}
                onDelete={handleDeleteStoreItem}
                isViewer={false}
              />
            </Box>
          ))}

        {openPrizeDrawDialog && (
          <StoreItemDialog
            item={item}
            open={openPrizeDrawDialog}
            onSuccessHandler={handleSuccessStageDialog}
            onClose={handleCloseStageDialog}
          />
        )}
      </Container>
    </>
  );
}
